import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CtaSection from "@/components/home/CtaSection";
import FutureServices from "@/components/home/FutureServices";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/lib/content";
import { iconFor } from "@/components/home/WhatWeBuild";

export default function Services() {
  const { data } = useSite();
  const services = data?.services || [];
  const seo = data?.settings?.seo?.["/services"];

  return (
    <>
      <Seo
        title={seo?.title || "Services | Custom Software, Web Apps & SaaS — LogiTech Nexus"}
        siteName="LogiTech Nexus"
        description={seo?.description || "Explore LogiTech Nexus services: custom software development, web application development, SaaS platforms, workflow automation and system integrations."}
      />
      <PageHero
        eyebrow="Services"
        title="What LogiTech Nexus"
        accent="Can Build For You."
        subtitle="Software development is our core capability — delivered as a connected set of services designed around your business."
        testid="services-hero"
      />

      <section className="bg-void pb-28" data-testid="services-list">
        <div className="mx-auto max-w-7xl space-y-6 px-6 lg:px-10">
          {services.map((s, i) => {
            const Icon = iconFor(s.icon);
            return (
              <Reveal key={s.id || s.slug} delay={Math.min(i * 0.05, 0.3)} y={24}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-row-${s.slug}`}
                  className="group grid gap-6 rounded-2xl border border-slate-800/80 bg-navy/60 p-8 transition-[border-color,background-color] duration-500 hover:border-blue-500/40 hover:bg-navy sm:grid-cols-[auto_1fr_auto] sm:items-center"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-blue-500/25 bg-blue-500/10 text-blue-400">
                    <Icon size={24} />
                  </span>
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">{s.title}</h2>
                      {s.expanding && (
                        <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                          Expanding Capability
                        </span>
                      )}
                    </div>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">{s.tagline}</p>
                    {s.bullets?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {s.bullets.map((b) => (
                          <span key={b} className="rounded-full border border-slate-800 bg-slate-900/60 px-3 py-1 text-xs text-slate-400">
                            {b}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="hidden items-center gap-2 text-sm font-semibold text-blue-400 sm:inline-flex">
                    Explore
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <FutureServices />
      <CtaSection />
    </>
  );
}
